import { Box, IconButton, MediaOverlay, Tooltip, TooltipProps } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { DeleteSmall, PlaySmall } from '@wix/wix-ui-icons-common';
import React, { FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Attachment, AttachmentType, TLocalesKeys } from '../../../../../types';
import { attachesSlice } from '../../../../common/reducers/attaches';
import s from '../styles.scss';
import { AttachmentPreviewDuration } from './AttachmentPreviewDuration';
import { AttachmentPreviewImage } from './AttachmentPreviewImage';
import { attachmentsPreviewDataHooks } from './AttachmentsPreview.dataHooks';

type AttachmentPreviewProps = Readonly<{
  attachment: Attachment;
}>;

const moveByTooltipProps: TooltipProps['moveBy'] = { y: 6 };

export const AttachmentPreview: FC<AttachmentPreviewProps> = ({ attachment }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isVideo = attachment.type === AttachmentType.Video;
  const showDuration = isVideo && attachment.previewLoaded && !attachment.previewFailed;

  const onRemove = useCallback(() => {
    dispatch(attachesSlice.actions.removeAttachment(attachment.url));
  }, [dispatch, attachment]);

  const classNames = isVideo ? s.videoMediaOverlay : s.imageMediaOverlay;

  return (
    <Box flexShrink={0} overflow="hidden">
      <MediaOverlay
        className={classNames}
        hoverSkin="dark"
        borderRadius="6px"
        onClick={onRemove}
        dataHook={attachmentsPreviewDataHooks.mediaOverlay()}
        media={<AttachmentPreviewImage attachment={attachment} />}
      >
        {showDuration && (
          <MediaOverlay.Content placement="bottom-end" visible="always">
            <Box className={s.videoDuration} color="D75" verticalAlign="middle" pointerEvents="none">
              <PlaySmall />
              <AttachmentPreviewDuration duration={attachment.duration} name={attachment.name} />
            </Box>
          </MediaOverlay.Content>
        )}
        <MediaOverlay.Content placement="middle" visible="hover">
          <Tooltip
            content={t<TLocalesKeys>('attach_file_remove_tooltip')}
            size="small"
            moveBy={moveByTooltipProps}
          >
            <IconButton
              size="small"
              skin="light"
              priority="secondary"
              onClick={onRemove}
              dataHook={attachmentsPreviewDataHooks.deleteButton()}
            >
              <DeleteSmall />
            </IconButton>
          </Tooltip>
        </MediaOverlay.Content>
      </MediaOverlay>
    </Box>
  );
};
